<script setup lang="ts">
import { ref } from "vue";
import { isNullish, TuInputLabel } from "@tinyurldev/tu-ui-vue";

import ValidationMessage from "@Components/forms/ValidationMessage.vue";

//
// Props
interface Props {
  value: string[];
  tagHistory?: string[];
  disabled?: boolean;
  tagError?: string;
}

const props = withDefaults(defineProps<Props>(), {
  tagHistory: () => [],
  disabled: false,
  tagError: undefined,
});

//
// Emit
interface Events {
  (e: "input", tags: string[]): void;
}

const emit = defineEmits<Events>();

//
// Data
const currentTag = ref("");
const currentTagRef = ref<HTMLInputElement | null>(null);
const localError = ref<string | null>(null);

//
// Methods
function validateInputTag(): string | null {
  const tag = currentTag.value;

  if (props.value.includes(tag)) {
    return "The tag has already been taken."
  }

  if (tag.length < 2) {
    return "The tag must be at least 2 characters.";
  }

  if (tag.length > 45) {
    return "The tag must not be greater than 45 characters.";
  }

  return null;
}

function addTag(): void {
  const error = validateInputTag();
  localError.value = null;

  if (!isNullish(error)) {
    localError.value = error;

    currentTagRef.value?.focus();

    return;
  }

  emit("input", [...props.value, currentTag.value]);
  currentTag.value = "";
}

function removeTag(filteredTag: string): void {
  emit("input", props.value.filter((tag) => tag !== filteredTag));
}

function handleTagInput(event: Event): void {
  const target = event.target as HTMLInputElement;
  currentTag.value = target.value.toLowerCase();
}

function commitPendingTag(): void {
  if (currentTag.value === "") {
    return;
  }

  addTag();
}

//
// Exposed Test Vars
defineExpose({ localError, commitPendingTag });
</script>

<template>
  <div class="tags">
    <TuInputLabel
      id="current-tag"
      class="tags__label"
      icon="fa-solid fa-tag"
      :label="`${$t('add_tags')} (Optional)`"
    />

    <div class="tags__list">
      <button
        v-for="tag in value"
        :key="tag"
        type="button"
        data-test-id="dashboard_create_tinyurl_field_tag"
        class="btn tags__tag"
        @click="removeTag(tag)"
        @keydown.esc.stop.prevent="removeTag(tag)"
        @keydown.backspace.stop.prevent="removeTag(tag)"
      >
        <span data-test-id="dashboard_create_tinyurl_field_tag_text">
          {{ tag }}
        </span>
        <i
          data-test-id="dashboard_create_tinyurl_field_tag_btn"
          class="fa-solid fa-times ml-2"
        />
      </button>

      <div class="tags__input-wrapper">
        <input
          id="current-tag"
          ref="currentTagRef"
          v-model="currentTag"
          list="tag-history"
          name="current-tag"
          type="text"
          class="form-control tags__input _tag_input"
          :class="{ '_tags-input-disabled': disabled }"
          :disabled="disabled"
          placeholder="Enter Tag Name"
          @input="handleTagInput"
          @keypress.prevent.enter="addTag"
        >
        <datalist id="tag-history">
          <option
            v-for="tag in tagHistory"
            :key="tag"
            :value="tag"
          />
        </datalist>
        <button
          data-test-id="dashboard_create_tinyurl_add_tag_btn"
          class="btn tags__add-btn _add_tag_btn"
          type="button"
          @click="addTag"
        >
          <i class="fa-solid fa-plus fa-lg text-blue" />
        </button>
      </div>
    </div>

    <ValidationMessage
      v-if="localError || tagError"
      class="_error-message"
      :message="localError ?? tagError"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.tags {
  &__label {
    margin-bottom: spacing.$xxxs;
  }

  &__list {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 10px;
  }

  &__tag {
    background-color: color.$background-overlay;
    color: color.$text;
    display: flex;
    align-items: center;
  }

  &__input-wrapper {
    display: flex;
    align-items: center;
    max-width: 175px;
  }

  &__input {
    color: color.$text;
    margin-right: spacing.$xxxs;
    padding: spacing.$xxxxs spacing.$xxxs;
    width: 154px;

    &:focus {
      color: color.$text;
      border-color: color.$secondary;
      box-shadow: 0 0 0 0.2rem color.$secondary-light;
    }

    &::placeholder {
      color: color.$text-secondary;
      font-weight: typography.$font-weight-medium;
    }

    &::-webkit-calendar-picker-indicator {
      display: none !important;
    }
  }

  &__add-btn {
    font-weight: typography.$font-weight-medium;
    padding: 0;

    &:focus {
      color: color.$text;
      border-color: color.$secondary;
      box-shadow: 0 0 0 0.2rem color.$secondary-light;
    }
  }
}
</style>
